// Practice sounds on top of playback (F3.6 count-in, F3.7 metronome, F3.8 chord synth,
// F9.1 speed trainer). Created per song by the player store, next to the Transport.
import type { ChordEvent, ClickEvent } from '@/core/practice/events';
import { SoundScheduler } from './soundScheduler';
import { playChord, playClick } from './sounds';
import type { Transport } from './transport';

export interface SpeedTrainer {
  /** Speeds, as rates (0.5–1.5). */
  start: number;
  target: number;
  /** Added after each pass through the loop. */
  step: number;
}

export class Practice {
  private readonly metronomeBus: GainNode;
  private readonly synthBus: GainNode;
  private readonly countInBus: GainNode;
  private readonly metronome: SoundScheduler<ClickEvent>;
  private readonly synth: SoundScheduler<ChordEvent>;
  private countInNodes: AudioScheduledSourceNode[] = [];
  private trainer: SpeedTrainer | null = null;
  private lastPosition = 0;
  /** Loop-pass detection waits until a new schedule is audible (seeks aren't passes). */
  private settleUntil = 0;
  private readonly unsubscribe: (() => void)[] = [];
  private readonly watch: ReturnType<typeof setInterval>;

  constructor(
    private readonly ctx: AudioContext,
    private readonly transport: Transport,
    /** Called when the speed trainer moves to the next speed. */
    private readonly onTrainerRate: (rate: number) => void,
  ) {
    this.metronomeBus = ctx.createGain();
    this.synthBus = ctx.createGain();
    this.metronomeBus.connect(ctx.destination);
    this.synthBus.connect(ctx.destination);
    this.countInBus = ctx.createGain();
    this.countInBus.connect(ctx.destination);
    this.metronome = new SoundScheduler(ctx, transport, (e, when) =>
      playClick(ctx, this.metronomeBus, when, e.accent),
    );
    this.synth = new SoundScheduler(ctx, transport, (e, when, next) =>
      playChord(ctx, this.synthBus, when, e.notes, next - when),
    );
    this.unsubscribe.push(
      transport.on('change', () => {
        this.settleUntil = transport.anchor.output + 0.05;
      }),
      transport.on('state', () => {
        if (transport.state === 'paused') this.cancelCountIn();
      }),
    );
    // Speed trainer: a pass is complete when the heard position jumps back to the loop start.
    this.watch = setInterval(() => {
      this.checkLoopPass();
    }, 50);
  }

  setMetronome(on: boolean, volume: number, events: ClickEvent[]): void {
    this.metronomeBus.gain.value = volume;
    this.metronome.setEvents(events);
    if (on) this.metronome.start();
    else this.metronome.stop();
  }

  setSynth(on: boolean, volume: number, events: ChordEvent[]): void {
    this.synthBus.gain.value = volume;
    this.synth.setEvents(events);
    if (on) this.synth.start();
    else this.synth.stop();
  }

  /** Starts the speed trainer at its start speed; null turns it off. */
  setTrainer(trainer: SpeedTrainer | null): void {
    this.trainer = trainer;
    if (trainer) this.onTrainerRate(trainer.start);
  }

  /**
   * Plays one bar of clicks (F3.6) and starts playback right after the last one.
   * `beatSeconds` is one beat at the current speed.
   */
  countInAndPlay(beats: number, beatSeconds: number, volume: number): void {
    this.cancelCountIn();
    const first = this.ctx.currentTime + 0.1;
    this.countInBus.gain.value = volume;
    for (let i = 0; i < beats; i++) {
      this.countInNodes.push(
        ...playClick(this.ctx, this.countInBus, first + i * beatSeconds, i === 0),
      );
    }
    this.transport.play(first + beats * beatSeconds);
  }

  dispose(): void {
    clearInterval(this.watch);
    for (const off of this.unsubscribe) off();
    this.cancelCountIn();
    this.metronome.dispose();
    this.synth.dispose();
    this.metronomeBus.disconnect();
    this.synthBus.disconnect();
    this.countInBus.disconnect();
  }

  private cancelCountIn(): void {
    for (const node of this.countInNodes) {
      try {
        node.stop();
      } catch {
        // Already played.
      }
    }
    this.countInNodes = [];
  }

  private checkLoopPass(): void {
    const t = this.transport;
    const now = this.ctx.currentTime;
    const position = t.positionAt(now);
    if (now < this.settleUntil) {
      this.lastPosition = position;
      return;
    }
    const wrapped = t.loop !== null && t.state === 'playing' && position < this.lastPosition - 0.05;
    this.lastPosition = position;
    if (!wrapped || !this.trainer) return;
    const next = Math.min(this.trainer.target, t.rate + this.trainer.step);
    if (next > t.rate + 1e-6) this.onTrainerRate(next);
  }
}
